/**
 * Forward Valuation (Unified)
 * Combines MtM calculation and manual Excel upload into a single page.
 * Batch selector shows historical valuation runs; results displayed as flat grid.
 * Shows all Forward trades including unvalued ones with workflow state indication.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useAuth } from '../../components/auth/AuthContext';
import { valuationService } from '../../services/api/valuationService';
import { accountingService } from '../../services/api/accountingService';
import { workflowService } from '../../services/api/workflowService';
import { clientService, UnmatchedTrade } from '../../services/clientService';
import { clientService as apiClientService, ValuationPreferences } from '../../services/api/clientService';
import { exportValuationToExcel } from '../../utils/valuationExcelExport';
import {
  TradeWithValuation,
  ValuationRunResult,
  ValuationUpload,
  ValuationBatchResult,
  CurveSummary,
  FwdPointCurveSummary,
} from '../../types/valuation';
import { ValuationUploadResult } from '../../types/accounting';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import Toast from '../../components/common/Toast';
import { useAiContextMenu } from '../../hooks/useAiContextMenu';
import InlineMenu from '../../components/common/InlineMenu';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/App.css';
import '../admin/Accounting.css';

type ValuationMethod = 'calculate' | 'manual';

/** Extended trade type that includes workflow state and valuation status */
interface DisplayTrade extends TradeWithValuation {
  workflowState?: string;
  isUnvalued?: boolean;
  skipReason?: string;
}

const ELIGIBLE_STATES = ['Confirmed', 'ContractSigned', 'Final'];

/** Format YYYY-MM-DD → DD/MM/YYYY */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

/** Format ISO datetime → DD/MM/YYYY HH:mm */
const formatDateTime = (isoStr: string): string => {
  if (!isoStr) return '\u2014';
  try {
    const d = new Date(isoStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoStr;
  }
};

/** Get previous business day (skip weekends) from a YYYY-MM-DD string */
const getPreviousBusinessDay = (dateStr: string): string => {
  const d = new Date(dateStr + 'T12:00:00');
  do {
    d.setDate(d.getDate() - 1);
  } while (d.getDay() === 0 || d.getDay() === 6); // skip Sun/Sat
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface ValuationPositionsProps {
  /** System date (YYYY-MM-DD) — used as default valuation date (user can override) */
  systemDate?: string;
  /** Navigation callbacks — passed from Valuation.tsx to switch Market Data sub-tabs */
  onNavigateToIrCurves?: () => void;
  onNavigateToFwdPoints?: () => void;
  onNavigateToSpotRates?: () => void;
}

const ValuationPositions: React.FC<ValuationPositionsProps> = ({
  systemDate,
  onNavigateToIrCurves,
  onNavigateToFwdPoints,
  onNavigateToSpotRates,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);
  const clientId = user?.organization?.id || '';

  // Method selector
  const [method, setMethod] = useState<ValuationMethod>('calculate');

  // Calculate controls
  const [valuationDate, setValuationDate] = useState(systemDate || '');
  const [forceRecalculate, setForceRecalculate] = useState(false);

  // Update valuation date when systemDate arrives from Firestore
  useEffect(() => {
    if (systemDate && !valuationDate) {
      setValuationDate(systemDate);
    }
  }, [systemDate]); // eslint-disable-line react-hooks/exhaustive-deps
  const [forwardRateSource, setForwardRateSource] = useState<'cirp' | 'fwd_points'>('cirp');
  const [discountingMethod, setDiscountingMethod] = useState<'two_leg' | 'net_pnl'>('net_pnl');
  const [fwdPointsPrice, setFwdPointsPrice] = useState<'market' | 'mid' | 'bid' | 'ask'>('market');
  const [valuationCurrency, setValuationCurrency] = useState<string>('CLP');
  const [asiaticoMethod, setAsiaticoMethod] = useState<'blended' | 'single_rate'>('blended');
  const [forwardStartingEligibility, setForwardStartingEligibility] = useState<'confirmed_only' | 'include_provisional'>('confirmed_only');
  const [forwardStartingMethod, setForwardStartingMethod] = useState<'single_rate' | 'blended'>('single_rate');
  const [splitMtmLevel, setSplitMtmLevel] = useState<'parent' | 'split'>('parent');
  const [ufSpotDateMode, setUfSpotDateMode] = useState<'inherit' | 'furthest_known' | 'execution_date' | 'as_of_date' | 'first_business_day'>('execution_date');
  const [spotDateMode, setSpotDateMode] = useState<'curve_date' | 'execution_date' | 'as_of_date' | 'first_business_day'>('execution_date');
  const [valuationDateMode, setValuationDateMode] = useState<'system_date' | 'previous_business_day' | 'end_of_previous_month'>('system_date');
  const [marketDataDateMode, setMarketDataDateMode] = useState<'system_date' | 'valuation_date' | 'first_business_day'>('system_date');
  const [methodologyExpanded, setMethodologyExpanded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<ValuationRunResult | null>(null);
  const [availableCurves, setAvailableCurves] = useState<CurveSummary[]>([]);
  const [availableFwdPointCurves, setAvailableFwdPointCurves] = useState<FwdPointCurveSummary[]>([]);
  const [spotRateAvailability, setSpotRateAvailability] = useState<Record<string, boolean>>({});
  const [loadingForwardTrades, setLoadingForwardTrades] = useState(true);
  const [loadingCurves, setLoadingCurves] = useState(true);
  const [loadingSpotRates, setLoadingSpotRates] = useState(true);

  // Manual upload controls
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lastUploadResult, setLastUploadResult] = useState<ValuationUploadResult | null>(null);
  const [showUploadErrors, setShowUploadErrors] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Trade data
  const [valuedTrades, setValuedTrades] = useState<TradeWithValuation[]>([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [allForwardTrades, setAllForwardTrades] = useState<UnmatchedTrade[]>([]);

  // Upload history (used to derive valuation dates)
  const [uploads, setUploads] = useState<ValuationUpload[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [executionExpanded, setExecutionExpanded] = useState(false);
  // Batch selector (null = latest/current view, string = selected uploadId)
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchData, setBatchData] = useState<ValuationBatchResult | null>(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  // Collapse state (unvalued collapsed by default)
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set(['unvalued']));

  // Demo mode
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Groupings config (for dynamic column headers)
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // Modals
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [confirmDeleteUpload, setConfirmDeleteUpload] = useState<ValuationUpload | null>(null);
  const [deleteCheckResult, setDeleteCheckResult] = useState<{ hasAccountingEvents: boolean; accountingEventCount: number } | null>(null);
  const [checkingDelete, setCheckingDelete] = useState(false);
  const [deletingUploadId, setDeletingUploadId] = useState<string | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Resolved date info from backend (KAN-223)
  const [resolvedDateInfo, setResolvedDateInfo] = useState<{
    asOfDate: string;
    isMonthEnd: boolean;
    businessCenter: string;
  } | null>(null);
  const [resolvingDate, setResolvingDate] = useState(false);

  // Blended rate detail popover (Asiático trades)
  const [blendedRateDetail, setBlendedRateDetail] = useState<{ trade: any; position: { x: number; y: number } } | null>(null);
  const blendedRateDetailRef = useRef<HTMLDivElement>(null);

  // AG Grid ref + container ref (for AI context menu)
  const valuationGridRef = useRef<AgGridReact>(null);
  const valuationGridContainerRef = useRef<HTMLDivElement>(null);

  // AI Context Menu — Valuation grid (with blended rate composition)
  const {
    showMenu: showAiMenu,
    menuPosition: aiMenuPosition,
    menuItems: aiMenuItems,
    closeMenu: closeAiMenu,
  } = useAiContextMenu({
    objectType: 'valuation',
    gridRef: valuationGridRef,
    containerRef: valuationGridContainerRef,
    getPlaceholders: (row) => ({
      trade_id: row.tradeNumber,
      direction: row.direction,
      ccy_pair: `${row.currency1}/${row.currency2}`,
      fecha_vencimiento: (row.maturityDate || row.valueDate || '').split('-').reverse().join('/'),
      batch_id: row.currentValuation?.uploadId || '',
    }),
    onCopied: () => setToast({ message: t('aiContext.copied', 'Pregunta copiada — pégala en tu asistente de IA.'), type: 'success' }),
    onError: () => setToast({ message: t('aiContext.copyFailed', 'No se pudo copiar al portapapeles.'), type: 'error' }),
    onBeforeShow: (rowData, event, cellElement) => {
      // Intercept: show blended rate popup for Asiático trades on the Forward Rate column
      const isAsiatico = rowData?.isAverageRateForward || rowData?.IsAverageRateForward;
      const cv = rowData?.currentValuation;
      if (!isAsiatico || !cv?.perDateRates || cv.perDateRates.length === 0) return false;
      const colId = cellElement.getAttribute('col-id');
      if (colId !== 'forwardRate') return false;
      // Show blended rate popup instead of AI menu
      setBlendedRateDetail({ trade: rowData, position: { x: event.clientX, y: event.clientY } });
      return true;
    },
  });

  // ============================================
  // Data loading
  // ============================================

  const loadValuedTrades = useCallback(async () => {
    if (!clientId) return;
    setLoadingTrades(true);
    try {
      const response = await valuationService.getLatestValuations(clientId);
      setValuedTrades(response.data || []);
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.positions.loadError'), type: 'error' });
    } finally {
      setLoadingTrades(false);
    }
  }, [clientId, t]);

  const loadAllForwardTrades = useCallback(async () => {
    if (!clientId) return;
    setLoadingForwardTrades(true);
    try {
      const trades = await clientService.getForwardTrades(clientId);
      setAllForwardTrades(trades || []);
    } catch {
      // Non-critical — unvalued trades just won't show
    } finally {
      setLoadingForwardTrades(false);
    }
  }, [clientId]);

  const loadUploads = useCallback(async () => {
    if (!clientId) return;
    setLoadingUploads(true);
    try {
      const response = await valuationService.getValuationUploads(clientId, 20);
      setUploads(response.data || []);
    } catch {
      // Non-critical
    } finally {
      setLoadingUploads(false);
    }
  }, [clientId]);

  // Resolve as-of date via backend business calendar (KAN-223)
  useEffect(() => {
    if (!clientId || !valuationDate) {
      setResolvedDateInfo(null);
      return;
    }
    if (valuationDateMode === 'system_date') {
      // No API call needed — as-of date equals execution date
      setResolvedDateInfo({
        asOfDate: valuationDate,
        isMonthEnd: false,
        businessCenter: '',
      });
      return;
    }
    let cancelled = false;
    setResolvingDate(true);
    valuationService.resolveValuationDate(clientId, valuationDate, valuationDateMode)
      .then(response => {
        if (cancelled) return;
        if (response.success && response.data) {
          setResolvedDateInfo(response.data);
        }
      })
      .catch(() => { /* Non-critical — local fallback used */ })
      .finally(() => { if (!cancelled) setResolvingDate(false); });
    return () => { cancelled = true; };
  }, [clientId, valuationDate, valuationDateMode]);

  // Resolved as-of date: prefer API response, fall back to local estimate
  const resolvedAsOfDate = useMemo(() => {
    if (!valuationDate) return '';
    if (resolvedDateInfo?.asOfDate) return resolvedDateInfo.asOfDate;
    // Local fallback while API is in flight
    if (valuationDateMode === 'previous_business_day') return getPreviousBusinessDay(valuationDate);
    if (valuationDateMode === 'end_of_previous_month') {
      // Rough local estimate: last day of previous month (no holiday calendar)
      const d = new Date(valuationDate + 'T12:00:00');
      const lastDay = new Date(d.getFullYear(), d.getMonth(), 0);
      return `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    }
    return valuationDate;
  }, [valuationDate, valuationDateMode, resolvedDateInfo]);

  const resolvedMarketDataDate = useMemo(() => {
    if (!valuationDate) return '';
    if (marketDataDateMode === 'valuation_date') return resolvedAsOfDate;
    if (marketDataDateMode === 'first_business_day') {
      const d = new Date(valuationDate + 'T12:00:00');
      const first = new Date(d.getFullYear(), d.getMonth(), 1, 12);
      while (first.getDay() === 0 || first.getDay() === 6) first.setDate(first.getDate() + 1);
      return `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}-${String(first.getDate()).padStart(2, '0')}`;
    }
    return valuationDate; // system_date
  }, [valuationDate, marketDataDateMode, resolvedAsOfDate]);

  // Resolve spot date for display (mirrors backend logic)
  const resolvedSpotDate = useMemo(() => {
    if (!valuationDate) return '';
    if (spotDateMode === 'as_of_date') return resolvedAsOfDate;
    if (spotDateMode === 'first_business_day') {
      const d = new Date(valuationDate + 'T12:00:00');
      const first = new Date(d.getFullYear(), d.getMonth(), 1, 12);
      while (first.getDay() === 0 || first.getDay() === 6) first.setDate(first.getDate() + 1);
      return `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}-${String(first.getDate()).padStart(2, '0')}`;
    }
    return valuationDate; // execution_date / curve_date
  }, [valuationDate, spotDateMode, resolvedAsOfDate]);

  // Resolve UF date for display (mirrors backend logic)
  const resolvedUfDate = useMemo(() => {
    if (!valuationDate) return '';
    if (ufSpotDateMode === 'as_of_date') return resolvedAsOfDate;
    if (ufSpotDateMode === 'first_business_day') {
      const d = new Date(valuationDate + 'T12:00:00');
      const first = new Date(d.getFullYear(), d.getMonth(), 1, 12);
      while (first.getDay() === 0 || first.getDay() === 6) first.setDate(first.getDate() + 1);
      return `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}-${String(first.getDate()).padStart(2, '0')}`;
    }
    if (ufSpotDateMode === 'furthest_known') {
      const d = new Date(valuationDate + 'T12:00:00');
      if (d.getDate() <= 9) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-09`;
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 9, 12);
      return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-09`;
    }
    return valuationDate; // execution_date / inherit
  }, [valuationDate, ufSpotDateMode, resolvedAsOfDate]);

  const loadCurvesForDate = useCallback(async () => {
    if (!clientId || !resolvedMarketDataDate) return;
    setLoadingCurves(true);
    try {
      const [irResponse, fwdResponse] = await Promise.all([
        valuationService.getCurves(clientId, resolvedMarketDataDate),
        valuationService.getFwdPointCurves(clientId, resolvedMarketDataDate),
      ]);
      setAvailableCurves(irResponse.data || []);
      setAvailableFwdPointCurves(fwdResponse.data || []);
    } catch {
      setAvailableCurves([]);
      setAvailableFwdPointCurves([]);
    } finally {
      setLoadingCurves(false);
    }
  }, [clientId, resolvedMarketDataDate]);

  const loadSpotRates = useCallback(async () => {
    if (!clientId || !resolvedMarketDataDate || allForwardTrades.length === 0) return;
    const pairs = Array.from(
      new Set(
        allForwardTrades
          .filter(t => {
            const state = (t as any).WorkflowState || (t as any).workflowState || t.status || '';
            return ELIGIBLE_STATES.includes(state);
          })
          .map(t => {
            const ccy1 = (t as any).Currency1 || t.currency1 || '';
            const ccy2 = (t as any).Currency2 || t.currency2 || '';
            return `${ccy1}/${ccy2}`;
          })
          .filter(p => p !== '/')
      )
    );
    if (pairs.length === 0) { setLoadingSpotRates(false); return; }
    setLoadingSpotRates(true);
    try {
      const response = await valuationService.checkSpotRates(clientId, resolvedMarketDataDate, pairs);
      setSpotRateAvailability(response.data || {});
    } catch {
      setSpotRateAvailability({});
    } finally {
      setLoadingSpotRates(false);
    }
  }, [clientId, resolvedMarketDataDate, allForwardTrades]);

  useEffect(() => {
    if (clientId) {
      loadValuedTrades();
      loadAllForwardTrades();
      loadUploads();
    }
  }, [clientId, loadValuedTrades, loadAllForwardTrades, loadUploads]);

  useEffect(() => {
    loadCurvesForDate();
  }, [loadCurvesForDate]);

  useEffect(() => {
    loadSpotRates();
  }, [loadSpotRates]);

  // Load demo mode on mount
  useEffect(() => {
    const loadDemoMode = async () => {
      if (!clientId) return;
      try {
        const response = await workflowService.getWorkflowConfig(clientId);
        if (response.success && response.data) {
          setIsDemoMode(response.data.demoMode || false);
        }
      } catch (err) {
        console.warn('[ValuationPositions] Failed to load demo mode:', err);
      }
    };
    loadDemoMode();
  }, [clientId]);

  // Load saved valuation preferences → pre-fill methodology selectors
  useEffect(() => {
    const loadValuationPreferences = async () => {
      if (!clientId) return;
      try {
        const response = await apiClientService.getValuationPreferences(clientId);
        if (response.success && response.data) {
          const prefs = response.data;
          setForwardRateSource(prefs.forwardRateSource);
          setDiscountingMethod(prefs.discountingMethod);
          setFwdPointsPrice(prefs.fwdPointsPrice);
          setValuationCurrency(prefs.valuationCurrency);
          if (prefs.asiaticoMethod) setAsiaticoMethod(prefs.asiaticoMethod);
          if (prefs.forwardStartingEligibility) setForwardStartingEligibility(prefs.forwardStartingEligibility);
          if (prefs.forwardStartingMethod) setForwardStartingMethod(prefs.forwardStartingMethod);
          if (prefs.splitMtmLevel) setSplitMtmLevel(prefs.splitMtmLevel);
          if (prefs.ufSpotDateMode) setUfSpotDateMode(
            prefs.ufSpotDateMode === 'inherit' ? 'execution_date' : prefs.ufSpotDateMode
          );
          if (prefs.spotDateMode) setSpotDateMode(
            prefs.spotDateMode === 'curve_date' ? 'execution_date' : prefs.spotDateMode
          );
          if (prefs.valuationDateMode) setValuationDateMode(prefs.valuationDateMode);
          if (prefs.marketDataDateMode) setMarketDataDateMode(prefs.marketDataDateMode);
        }
      } catch {
        // Non-critical — methodology selectors fall back to local defaults
      }
    };
    loadValuationPreferences();
  }, [clientId]);

  // Load groupings config for dynamic column headers
  useEffect(() => {
    const loadGroupingsConfig = async () => {
      if (!clientId) return;
      try {
        const config = await groupingsService.getGroupings(clientId);
        setGroupingsConfig(config);
      } catch {
        // Non-critical — falls back to generic labels
      }
    };
    loadGroupingsConfig();
  }, [clientId]);

  // Dismiss blended rate detail popover on click-outside or Escape
  useEffect(() => {
    if (!blendedRateDetail) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (blendedRateDetailRef.current && !blendedRateDetailRef.current.contains(e.target as Node)) {
        setBlendedRateDetail(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBlendedRateDetail(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [blendedRateDetail]);

  // Position blended rate detail popover within viewport
  useEffect(() => {
    if (!blendedRateDetail || !blendedRateDetailRef.current) return;
    const el = blendedRateDetailRef.current;
    const rect = el.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;
    if (blendedRateDetail.position.x + rect.width > innerWidth) {
      el.style.left = `${blendedRateDetail.position.x - rect.width}px`;
    }
    if (blendedRateDetail.position.y + rect.height > innerHeight) {
      el.style.top = `${blendedRateDetail.position.y - rect.height}px`;
    }
  }, [blendedRateDetail]);

  // ============================================
  // Calculate MtM
  // ============================================

  const executeCalculate = async () => {
    if (!clientId || !valuationDate) return;
    setShowCalculateModal(false);
    setCalculating(true);
    setLastRunResult(null);
    setLastUploadResult(null);
    try {
      const response = await valuationService.calculateValuations(
        clientId, valuationDate, forceRecalculate, forwardRateSource, discountingMethod,
        fwdPointsPrice, valuationCurrency, asiaticoMethod,
        resolvedAsOfDate !== valuationDate ? resolvedAsOfDate : undefined,
        resolvedAsOfDate !== valuationDate ? resolvedMarketDataDate : undefined,
        forwardStartingEligibility, forwardStartingMethod, splitMtmLevel,
        ufSpotDateMode,
        spotDateMode,
        valuationDateMode,
        resolvedDateInfo?.businessCenter || undefined,
      );
      setLastRunResult(response.data);
      setToast({
        message: t('valuation.positions.calculateSuccess', { count: response.data.tradeCount }),
        type: response.data.tradeCount > 0 ? 'success' : 'info',
      });
      setSelectedBatchId(null); // Reset to latest view
      setBatchData(null);
      loadValuedTrades();
      loadAllForwardTrades();
      loadUploads();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.positions.calculateError'), type: 'error' });
    } finally {
      setCalculating(false);
    }
  };

  // ============================================
  // Manual upload
  // ============================================

  const handleFileUpload = async (file: File) => {
    if (!clientId) return;
    setUploadingFile(true);
    setLastUploadResult(null);
    setLastRunResult(null);
    setShowUploadErrors(false);
    try {
      const response = await accountingService.uploadValuations(clientId, file);
      if (response.success && response.data) {
        setLastUploadResult(response.data);
        const msg = response.data.success && response.data.errors.length === 0
          ? t('valuation.positions.calculateSuccess', { count: response.data.validRows })
          : response.data.validRows > 0
            ? t('valuation.positions.uploadResult', { valid: response.data.validRows, total: response.data.totalRows })
            : t('valuation.positions.calculateError');
        setToast({
          message: msg,
          type: response.data.validRows > 0 ? 'success' : 'error',
        });
        setSelectedBatchId(null);
        setBatchData(null);
        loadValuedTrades();
        loadAllForwardTrades();
        loadUploads();
      }
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.positions.calculateError'), type: 'error' });
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const name = file.name.toLowerCase();
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        handleFileUpload(file);
      } else {
        setToast({ message: t('valuation.positions.acceptedFormatsExcel'), type: 'error' });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // ============================================
  // Delete upload
  // ============================================

  const handleDeleteUploadClick = async (batch: ValuationUpload) => {
    if (!clientId) return;
    setCheckingDelete(true);
    setDeleteCheckResult(null);
    setConfirmDeleteUpload(batch);
    try {
      const response = await accountingService.checkDeleteValuationUpload(clientId, batch.id);
      setDeleteCheckResult({
        hasAccountingEvents: response.data.hasAccountingEvents,
        accountingEventCount: response.data.accountingEventCount,
      });
    } catch {
      // If check fails, still show modal without accounting warning
      setDeleteCheckResult(null);
    } finally {
      setCheckingDelete(false);
    }
  };

  const executeDeleteUpload = async () => {
    if (!confirmDeleteUpload || !clientId) return;
    const uploadId = confirmDeleteUpload.id;
    setConfirmDeleteUpload(null);
    setDeletingUploadId(uploadId);
    try {
      await accountingService.deleteValuationUpload(clientId, uploadId);
      setToast({ message: t('valuation.positions.deleteSuccess'), type: 'success' });
      setSelectedBatchId(null);
      setBatchData(null);
      loadValuedTrades();
      loadAllForwardTrades();
      loadUploads();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.positions.loadError'), type: 'error' });
    } finally {
      setDeletingUploadId(null);
      setDeleteCheckResult(null);
    }
  };

  // ============================================
  // Delete all valuations (demo mode only)
  // ============================================

  const executeDeleteAll = async () => {
    if (!clientId) return;
    setDeletingAll(true);
    setConfirmDeleteAll(false);
    try {
      const response = await valuationService.deleteAllValuations(clientId);
      const data = response.data;
      setToast({
        message: t('valuation.positions.deleteAllSuccess', {
          trades: data.tradesCleared,
          history: data.historyEntriesDeleted,
          uploads: data.uploadsDeleted,
        }),
        type: 'success',
      });
      setValuedTrades([]);
      setUploads([]);
      setSelectedBatchId(null);
      setBatchData(null);
      loadAllForwardTrades();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.positions.loadError'), type: 'error' });
    } finally {
      setDeletingAll(false);
    }
  };

  // ============================================
  // Batch selector (two-tier loading)
  // ============================================

  /** Available batches for the dropdown: uploads within last 12 months, sorted descending */
  const availableBatches = useMemo(() => {
    if (!systemDate) return uploads.filter(u => u.tradeCount > 0);
    const cutoff = new Date(systemDate + 'T00:00:00');
    cutoff.setMonth(cutoff.getMonth() - 12);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return uploads
      .filter(u => u.tradeCount > 0 && u.uploadDate >= cutoffStr)
      .sort((a, b) => b.uploadDate.localeCompare(a.uploadDate) || b.createdAt.localeCompare(a.createdAt));
  }, [uploads, systemDate]);

  /** Identify which uploadIds correspond to current/previous valuations on trade docs */
  const currentUploadId = useMemo(() => {
    // Find the most common uploadId among trades' currentValuation
    const counts = new Map<string, number>();
    valuedTrades.forEach(t => {
      const uid = t.currentValuation?.uploadId;
      if (uid) counts.set(uid, (counts.get(uid) || 0) + 1);
    });
    let maxId = '';
    let maxCount = 0;
    counts.forEach((count, id) => { if (count > maxCount) { maxCount = count; maxId = id; } });
    return maxId || null;
  }, [valuedTrades]);

  const previousUploadId = useMemo(() => {
    const counts = new Map<string, number>();
    valuedTrades.forEach(t => {
      const uid = t.previousValuation?.uploadId;
      if (uid) counts.set(uid, (counts.get(uid) || 0) + 1);
    });
    let maxId = '';
    let maxCount = 0;
    counts.forEach((count, id) => { if (count > maxCount) { maxCount = count; maxId = id; } });
    return maxId || null;
  }, [valuedTrades]);

  /** Handle batch selection with two-tier loading */
  const handleBatchSelect = useCallback(async (uploadId: string | null) => {
    setSelectedBatchId(uploadId);
    setBatchData(null);

    if (!uploadId || !clientId) return;

    // Tier 1: current or previous — data already on trade docs, no API call needed
    if (uploadId === currentUploadId || uploadId === previousUploadId) return;

    // Tier 2: historical — fetch from API
    setLoadingBatch(true);
    try {
      const response = await valuationService.getValuationBatch(clientId, uploadId);
      setBatchData(response.data || null);
    } catch {
      setToast({ message: t('valuation.positions.batchLoadError'), type: 'error' });
    } finally {
      setLoadingBatch(false);
    }
  }, [clientId, currentUploadId, previousUploadId, t]);

  // Auto-select the most recent batch when uploads load (or after reset)
  useEffect(() => {
    if (!selectedBatchId && availableBatches.length > 0) {
      // Don't trigger full handleBatchSelect since the first batch will be Tier 1 (current)
      setSelectedBatchId(availableBatches[0].id);
    }
  }, [availableBatches, selectedBatchId]);

  // ============================================
  // Formatting helpers
  // ============================================

  const formatNumber = (n: number | undefined | null, decimals: number = 2): string => {
    if (n === undefined || n === null) return '\u2014';
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatRate = (n: number | undefined | null): string => {
    if (n === undefined || n === null) return '\u2014';
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  /** Format amount with currency prefix */
  const formatMtm = (amount: number | undefined | null, currency?: string): string => {
    if (amount === undefined || amount === null) return '\u2014';
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? `${currency} ${formatted}` : formatted;
  };

  /** AG-Grid context menu handler — show blended rate breakdown for Asiático trades */
  // ============================================
  // Derived data
  // ============================================

  /** Derives what data is required to run valuation given the current methodology
   *  settings and active trade portfolio, then checks availability of each item. */
  const valuationRequirements = useMemo<Array<{ label: string; present: boolean; category: 'spot' | 'fwd' | 'ir' }>>(() => {
    // allForwardTrades arrives with PascalCase Firestore fields (Currency1, WorkflowState, etc.)
    const ccy1Of = (t: any): string => t.Currency1 || t.currency1 || '';
    const ccy2Of = (t: any): string => t.Currency2 || t.currency2 || '';
    const statusOf = (t: any): string => t.WorkflowState || t.workflowState || t.Status || t.status || '';

    const eligibleTrades = allForwardTrades.filter(t => ELIGIBLE_STATES.includes(statusOf(t)));
    const uniquePairs = Array.from(
      new Set(eligibleTrades.map(t => `${ccy1Of(t)}/${ccy2Of(t)}`).filter(p => p !== '/'))
    );
    if (uniquePairs.length === 0) return [];

    const irCurrencies = new Set(availableCurves.map(c => c.currency));
    // FwdPointCurveSummary.pairKey is stored without slash e.g. "USDCLP"
    const fwdPairKeys = new Set(availableFwdPointCurves.map(p => p.pairKey));

    const reqs: Array<{ label: string; present: boolean; category: 'spot' | 'fwd' | 'ir' }> = [];

    // 1. Spot rates — always required
    uniquePairs.forEach(pair => {
      const isClfPair = pair.includes('CLF');
      const ufNote = isClfPair && ufSpotDateMode === 'furthest_known' ? ' (furthest UF)' : '';
      reqs.push({ label: `${pair} Spot Rate${ufNote}`, present: spotRateAvailability[pair] ?? false, category: 'spot' });
    });

    // 2. Forward rate source
    if (forwardRateSource === 'fwd_points') {
      uniquePairs.forEach(pair => {
        const pairKey = pair.replace('/', '');
        reqs.push({ label: `${pair} Forward Points`, present: fwdPairKeys.has(pairKey), category: 'fwd' });
      });
    }

    // 3. IR discount curves
    const neededIr = new Set<string>();
    if (forwardRateSource === 'cirp') {
      // CIRP needs both currency curves for forward rate derivation
      eligibleTrades.forEach(t => { neededIr.add(ccy1Of(t)); neededIr.add(ccy2Of(t)); });
    }
    if (discountingMethod === 'net_pnl') {
      // Net P&L discounts at each trade's ccy2 (quote currency) — not the valuation currency
      eligibleTrades.forEach(t => { neededIr.add(ccy2Of(t)); });
    } else {
      // Two-leg needs both currency curves for discounting
      eligibleTrades.forEach(t => { neededIr.add(ccy1Of(t)); neededIr.add(ccy2Of(t)); });
    }
    neededIr.forEach(ccy => {
      reqs.push({ label: `${ccy} Discount Curve`, present: irCurrencies.has(ccy), category: 'ir' });
    });

    return reqs;
  }, [
    allForwardTrades, availableCurves, availableFwdPointCurves,
    spotRateAvailability, forwardRateSource, discountingMethod, valuationCurrency, ufSpotDateMode,
  ]);

  /** Merge valued trades with all forward trades to include unvalued ones.
   *  Note: getForwardTrades returns raw Firestore dicts with PascalCase fields
   *  (TradeNumber, Currency1, Direction, etc.) while valuedTrades use camelCase. */
  const mergedTrades = useMemo<DisplayTrade[]>(() => {
    // Build error lookup from last run result (tradeNumber → error message)
    const errorByTrade = new Map<string, string>();
    if (lastRunResult?.errors) {
      lastRunResult.errors.forEach(e => {
        if (e.tradeNumber) errorByTrade.set(e.tradeNumber, e.error);
      });
    }

    // Build lookup by TradeNumber (PascalCase from Firestore)
    const forwardByNumber = new Map<string, any>();
    allForwardTrades.forEach(ft => {
      const raw = ft as any;
      const tn = raw.TradeNumber || raw.tradeNumber || '';
      if (tn) forwardByNumber.set(tn, raw);
    });

    // Enrich valued trades with workflowState + tradeDate from forward trades
    const valued: DisplayTrade[] = valuedTrades.map(vt => {
      const ft = forwardByNumber.get(vt.tradeNumber);
      const raw = ft as any;
      return {
        ...vt,
        tradeDate: vt.tradeDate || (raw?.TradeDate || raw?.tradeDate || ''),
        workflowState: ft?.workflowState || '',
        isUnvalued: false,
      };
    });

    // Create unvalued entries for forward trades not in the valued set.
    // Exclude matured trades (MaturityDate <= as-of date) and trades entered after as-of date.
    const asOfDateStr = resolvedAsOfDate || valuationDate;
    const valDateObj = asOfDateStr ? new Date(asOfDateStr + 'T00:00:00') : null;
    const valuedNumbers = new Set(valuedTrades.map(vt => vt.tradeNumber));
    const unvalued: DisplayTrade[] = allForwardTrades
      .filter(ft => {
        const raw = ft as any;
        const tn = raw.TradeNumber || raw.tradeNumber || '';
        if (!tn || valuedNumbers.has(tn)) return false;
        // Filter out matured trades (DD-MM-YYYY or YYYY-MM-DD)
        if (valDateObj) {
          const mat = raw.MaturityDate || raw.maturityDate || '';
          if (mat) {
            const parts = mat.includes('-') ? mat.split('-') : [];
            const matDate = parts.length === 3
              ? (parts[0].length === 4
                  ? new Date(mat + 'T00:00:00')                          // YYYY-MM-DD
                  : new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`)) // DD-MM-YYYY
              : null;
            if (matDate && matDate <= valDateObj) return false;
          }
        }
        return true;
      })
      .map(ft => {
        const raw = ft as any;
        return {
          tradeId: ft.id,
          tradeNumber: raw.TradeNumber || raw.tradeNumber || '',
          productType: raw.ProductType || raw.productType || '',
          direction: raw.Direction || raw.direction || '',
          currency1: raw.Currency1 || raw.currency1 || '',
          currency2: raw.Currency2 || raw.currency2 || '',
          quantityCurrency1: raw.QuantityCurrency1 || raw.quantityCurrency1 || 0,
          quantityCurrency2: raw.QuantityCurrency2 || raw.quantityCurrency2 || 0,
          price: raw.Price || raw.ForwardPrice || raw.forwardPrice || 0,
          valueDate: raw.ValueDate || raw.valueDate || '',
          maturityDate: raw.MaturityDate || raw.maturityDate || '',
          settlementType: raw.SettlementType || raw.settlementType || '',
          settlementCurrency: raw.SettlementCurrency || raw.settlementCurrency || '',
          counterpartyName: raw.CounterpartyName || raw.counterpartyName || '',
          ourParty: raw.OurParty || raw.ourParty || '',
          portfolio: raw.Portfolio || '',
          grouping1: raw.Grouping1 || '',
          grouping2: raw.Grouping2 || '',
          grouping3: raw.Grouping3 || '',
          hedgeObject: raw.HedgeObject || raw.hedgeObject || '',
          tradeDate: raw.TradeDate || raw.tradeDate || '',
          isAverageRateForward: raw.IsAverageRateForward || raw.isAverageRateForward || false,
          isForwardStarting: raw.IsForwardStarting || raw.isForwardStarting || false,
          currentValuation: null,
          previousValuation: null,
          workflowState: raw.workflowState || 'Unknown',
          isUnvalued: true,
          skipReason: (() => {
            const tn = raw.TradeNumber || raw.tradeNumber || '';
            // Engine error from the current run takes priority
            const engineError = errorByTrade.get(tn);
            if (engineError) {
              if (engineError.includes('Settles on valuation date')) {
                return t('valuation.positions.settlesToday', 'Settles on valuation date — no MtM applicable');
              }
              return engineError;
            }
            // If workflow state is not eligible, that's the reason
            const ws = raw.workflowState || 'Unknown';
            if (!ELIGIBLE_STATES.includes(ws)) {
              return t('valuation.positions.notEligible', { state: ws });
            }
            // Check if trade date is after the as-of date
            const td = raw.TradeDate || raw.tradeDate || '';
            if (td && valDateObj) {
              const tdParts = td.includes('-') ? td.split('-') : [];
              const tdDate = tdParts.length === 3
                ? (tdParts[0].length === 4
                    ? new Date(td + 'T00:00:00')
                    : new Date(`${tdParts[2]}-${tdParts[1]}-${tdParts[0]}T00:00:00`))
                : null;
              if (tdDate && tdDate > valDateObj) {
                return t('valuation.positions.tradeDateAfterAsOf', {
                  tradeDate: formatDate(td),
                  asOfDate: formatDate(asOfDateStr),
                });
              }
            }
            return undefined;
          })(),
        };
      });

    return [...valued, ...unvalued];
  }, [valuedTrades, allForwardTrades, lastRunResult, t, valuationDate, resolvedAsOfDate]);

  /** Build minimal DisplayTrade rows for trades that errored during a batch run.
   *  These may no longer exist in mergedTrades (e.g. matured), so we construct from persisted errors. */
  const buildErrorRows = (
    engineErrorMap: Map<string, string>,
    valuedNumbers: Set<string>,
  ): DisplayTrade[] => {
    if (engineErrorMap.size === 0) return [];
    // Try to enrich from mergedTrades first; if not found, create a minimal stub
    const rows: DisplayTrade[] = [];
    engineErrorMap.forEach((error, tradeNumber) => {
      if (valuedNumbers.has(tradeNumber)) return; // already in valued set
      const existing = mergedTrades.find(t => t.tradeNumber === tradeNumber);
      if (existing) {
        rows.push({ ...existing, isUnvalued: true, skipReason: error });
      } else {
        // Trade no longer in current forward trades (e.g. matured) — minimal stub
        rows.push({
          tradeId: `error-${tradeNumber}`,
          tradeNumber,
          productType: 'Forward',
          direction: '',
          currency1: '',
          currency2: '',
          quantityCurrency1: 0,
          quantityCurrency2: 0,
          price: 0,
          valueDate: '',
          maturityDate: '',
          settlementType: '',
          settlementCurrency: '',
          counterpartyName: '',
          ourParty: '',
          portfolio: '',
          grouping1: '',
          grouping2: '',
          grouping3: '',
          hedgeObject: '',
          currentValuation: null,
          previousValuation: null,
          isUnvalued: true,
          skipReason: error,
        });
      }
    });
    return rows;
  };

  /** The trades to display for the selected batch (two-tier resolution) */
  const selectedBatchTrades = useMemo<DisplayTrade[] | null>(() => {
    if (!selectedBatchId) return null; // null = show default grouped view

    // Build error lookup from the selected upload's persisted errors (survives page refresh)
    // Fall back to lastRunResult for the current session (before errors are loaded from DB)
    const selectedUpload = uploads.find(u => u.id === selectedBatchId);
    const uploadErrors = selectedUpload?.errors || lastRunResult?.errors || [];
    const engineErrorMap = new Map<string, string>();
    uploadErrors.forEach(e => {
      if (e.tradeNumber) engineErrorMap.set(e.tradeNumber, e.error);
    });

    // Tier 1a: current batch — use trade.currentValuation + error rows
    if (selectedBatchId === currentUploadId) {
      const valuedInBatch = mergedTrades.filter(t => !t.isUnvalued && t.currentValuation?.uploadId === selectedBatchId);
      const valuedNumbers = new Set(valuedInBatch.map(t => t.tradeNumber));
      return [...valuedInBatch, ...buildErrorRows(engineErrorMap, valuedNumbers)];
    }

    // Tier 1b: previous batch — use trade.previousValuation mapped into currentValuation slot + error rows
    if (selectedBatchId === previousUploadId) {
      const valuedInBatch = mergedTrades
        .filter(t => !t.isUnvalued && t.previousValuation?.uploadId === selectedBatchId)
        .map(t => ({
          ...t,
          currentValuation: t.previousValuation,
          previousValuation: null,
        }));
      const valuedNumbers = new Set(valuedInBatch.map(t => t.tradeNumber));
      return [...valuedInBatch, ...buildErrorRows(engineErrorMap, valuedNumbers)];
    }

    // Tier 2: historical — use batchData from API
    if (!batchData) return null;
    return batchData.trades.map(ht => ({
      tradeId: ht.tradeId,
      tradeNumber: ht.tradeNumber,
      productType: 'Forward',
      direction: ht.direction,
      currency1: ht.currency1,
      currency2: ht.currency2,
      quantityCurrency1: ht.quantityCurrency1,
      quantityCurrency2: ht.quantityCurrency2,
      price: ht.price,
      valueDate: '',
      maturityDate: '',
      settlementType: ht.settlementType,
      settlementCurrency: '',
      counterpartyName: ht.counterpartyName,
      ourParty: '',
      portfolio: '',
      grouping1: '',
      grouping2: '',
      grouping3: '',
      hedgeObject: '',
      currentValuation: {
        currency: ht.currency || ht.currency2,
        mtmAmount: ht.mtmAmount,
        valuationDate: ht.valuationDate,
        uploadId: ht.uploadId,
        uploadedAt: ht.uploadedAt,
        mtmCcy1: ht.mtmCcy1,
        mtmCcy2: ht.mtmCcy2,
        daysToSettlement: ht.daysToSettlement,
        rCcy1: ht.rCcy1,
        rCcy2: ht.rCcy2,
        dfCcy1: ht.dfCcy1,
        dfCcy2: ht.dfCcy2,
        impliedForwardRate: ht.impliedForwardRate,
        contractedRate: ht.contractedRate,
        spotRate: ht.spotRate,
        dccCcy1: ht.dccCcy1,
        dccCcy2: ht.dccCcy2,
        calculatedBy: ht.calculatedBy,
        methodology: ht.methodology,
      },
      previousValuation: null,
      isUnvalued: false,
    }));
  }, [selectedBatchId, currentUploadId, previousUploadId, mergedTrades, batchData, lastRunResult, uploads]);

  /** Unvalued Forward trades (shown in their own section) */
  const unvaluedTrades = useMemo(() => {
    return mergedTrades.filter(t => t.isUnvalued);
  }, [mergedTrades]);

  /** Compute summary for a group of trades */
  const getGroupSummary = (trades: DisplayTrade[]) => {
    let totalMtm = 0;
    let totalChange = 0;
    let hasChange = false;
    let currency = '';
    let mixedCurrency = false;

    trades.forEach(trade => {
      if (trade.isUnvalued) return;
      const current = trade.currentValuation;
      const previous = trade.previousValuation;
      if (current) {
        const mtmVal = current.mtmCcy2 ?? current.mtmAmount ?? 0;
        totalMtm += mtmVal;
        const ccy = current.currency || trade.currency2 || '';
        if (!currency) currency = ccy;
        else if (ccy && ccy !== currency) mixedCurrency = true;
        if (previous) {
          const prevVal = previous.mtmCcy2 ?? previous.mtmAmount ?? 0;
          totalChange += mtmVal - prevVal;
          hasChange = true;
        }
      }
    });

    return { totalMtm, totalChange, hasChange, currency: mixedCurrency ? '' : currency };
  };

  const toggleDateGroup = (dateKey: string) => {
    setCollapsedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  // ============================================
  // AG Grid column definitions
  // ============================================

  /** Auto-size all columns to fit header + cell content */
  const onGridFirstDataRendered = useCallback((params: any) => {
    params.api.autoSizeAllColumns();
  }, []);

  /** Translate engine error messages stored in English in Firestore */
  const translateEngineError = useCallback((error: string): string => {
    // "No spot rate for USD/CLP on 2026-05-29"
    let m = error.match(/^No spot rate for (.+) on (.+)$/);
    if (m) return t('valuation.positions.engineNoSpotRate', { pair: m[1], date: m[2] });

    // "No IR curve found for CLP on 2026-05-29"
    m = error.match(/^No IR curve found for (.+) on (.+)$/);
    if (m) return t('valuation.positions.engineNoIRCurve', { ccy: m[1], date: m[2] });

    // "No forward point curve found for USD/CLP on 2026-05-29. Upload..."
    m = error.match(/^No forward point curve found for (.+) on (.+?)\./);
    if (m) return t('valuation.positions.engineNoFwdPointCurve', { pair: m[1], date: m[2] });

    // "Already valued for 2026-05-29"
    m = error.match(/^Already valued for (.+)$/);
    if (m) return t('valuation.positions.engineAlreadyValued', { date: m[1] });

    // "Settles on valuation date (2026-05-29) — no MtM applicable"
    m = error.match(/^Settles on valuation date \((.+?)\)/);
    if (m) return t('valuation.positions.engineSettlesToday', { date: m[1] });

    // Generic calculation error — pass through
    return t('valuation.positions.engineCalcError', { error });
  }, [t]);

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('valuation.positions.method'),
      minWidth: 80,
      pinned: 'left' as const,
      valueGetter: (params: any) => {
        if (params.data?.isUnvalued) return 'unvalued';
        return params.data?.currentValuation?.methodology ? 'calc' : 'manual';
      },
      cellRenderer: (params: any) => {
        if (params.value === 'unvalued') {
          const state = params.data?.workflowState || 'Unknown';
          const isEligible = ELIGIBLE_STATES.includes(state);
          return (
            <span
              title={isEligible ? t('valuation.positions.notYetValued') : t('valuation.positions.notEligible', { state })}
              style={{
                padding: '0.1rem 0.35rem',
                borderRadius: '3px',
                fontSize: '0.7rem',
                fontWeight: 600,
                background: isEligible ? 'rgba(255,152,0,0.15)' : 'rgba(117,117,117,0.15)',
                color: isEligible ? '#ffb74d' : '#9e9e9e',
              }}
            >
              {state}
            </span>
          );
        }
        const isCalc = params.value === 'calc';
        return (
          <span style={{
            padding: '0.1rem 0.35rem',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 600,
            background: isCalc ? 'rgba(79,195,247,0.15)' : 'rgba(156,39,176,0.15)',
            color: isCalc ? '#4fc3f7' : '#ce93d8',
          }}>
            {isCalc ? t('valuation.positions.calculated') : t('valuation.positions.manual')}
          </span>
        );
      },
      sortable: true,
      filter: true,
    },
    {
      headerName: t('valuation.positions.skipReason'),
      field: 'skipReason',
      minWidth: 140,
      valueGetter: (params: any) => params.data?.skipReason || null,
      cellRenderer: (params: any) => {
        if (!params.value) return null;
        const translated = translateEngineError(params.value);
        return (
          <span
            title={translated}
            style={{
              fontSize: '0.7rem',
              color: '#ffb74d',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '300px',
            }}
          >
            {translated}
          </span>
        );
      },
      sortable: true,
      filter: true,
    },
    {
      headerName: t('valuation.positions.tradeNumber'),
      field: 'tradeNumber',
      minWidth: 70,
      pinned: 'left' as const,
      sort: 'asc' as const,
    },
    {
      headerName: t('valuation.positions.ourPartyShort'),
      field: 'ourParty',
      minWidth: 70,
      pinned: 'left' as const,
    },
    {
      headerName: t('valuation.positions.direction'),
      field: 'direction',
      minWidth: 45,
      pinned: 'left' as const,
      cellStyle: (params: any) => ({
        color: params.value === 'Buy' ? '#2ecc71' : '#e74c3c',
        fontWeight: 600,
      }),
    },
    {
      headerName: t('valuation.positions.pair'),
      valueGetter: (params: any) => `${params.data?.currency1 || ''}/${params.data?.currency2 || ''}`,
      minWidth: 70,
      pinned: 'left' as const,
    },
    // --- Product (Forward vs Asiático vs Starting) ---
    {
      headerName: t('valuation.positions.product'),
      valueGetter: (params: any) => {
        if (params.data?.isAverageRateForward || params.data?.IsAverageRateForward) {
          return 'Forward (Asiático)';
        }
        if (params.data?.isForwardStarting || params.data?.IsForwardStarting) {
          return 'Forward (Starting)';
        }
        return 'Forward';
      },
      minWidth: 90,
    },
    // --- Shared columns ---
    {
      headerName: t('valuation.positions.settlementType'),
      field: 'settlementType',
      minWidth: 80,
    },
    {
      headerName: t('valuation.positions.amount'),
      field: 'quantityCurrency1',
      minWidth: 90,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value, 0),
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.contraAmount'),
      field: 'quantityCurrency2',
      minWidth: 90,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value, 0),
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.price'),
      field: 'price',
      minWidth: 60,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatRate(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.counterparty', 'Counterparty'),
      field: 'counterpartyName',
      minWidth: 100,
    },
    {
      headerName: t('valuation.positions.tradeDate', 'Trade Date'),
      field: 'tradeDate',
      minWidth: 90,
      valueFormatter: (params: any) => {
        if (!params.value) return '\u2014';
        return formatDate(params.value);
      },
    },
    {
      headerName: t('valuation.positions.valueDate', 'Value Date'),
      field: 'valueDate',
      minWidth: 90,
      valueFormatter: (params: any) => {
        if (!params.value) return '\u2014';
        return formatDate(params.value);
      },
    },
    {
      headerName: t('valuation.positions.maturityDate'),
      field: 'maturityDate',
      minWidth: 90,
      valueFormatter: (params: any) => formatDate(params.value || ''),
    },
    {
      headerName: groupingsConfig?.grouping1Label || t('grid.columns.grouping1'),
      field: 'grouping1',
      minWidth: 90,
    },
    {
      headerName: groupingsConfig?.grouping2Label || t('grid.columns.grouping2'),
      field: 'grouping2',
      minWidth: 90,
    },
    {
      headerName: groupingsConfig?.grouping3Label || t('grid.columns.grouping3'),
      field: 'grouping3',
      minWidth: 90,
    },
    {
      headerName: t('valuation.positions.hedgeObject'),
      field: 'hedgeObject',
      minWidth: 120,
    },
    {
      headerName: t('valuation.positions.valuationDate'),
      valueGetter: (params: any) => params.data?.currentValuation?.valuationDate,
      minWidth: 85,
      valueFormatter: (params: any) => formatDate(params.value || ''),
    },
    {
      headerName: t('valuation.positions.mtm'),
      valueGetter: (params: any) => params.data?.currentValuation?.mtmCcy2 ?? params.data?.currentValuation?.mtmAmount ?? null,
      minWidth: 120,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        const currency = params.data?.currentValuation?.currency || params.data?.currency2 || '';
        return formatMtm(params.value, currency);
      },
      cellStyle: (params: any) => ({
        fontFamily: 'monospace',
        fontWeight: 600,
        color: params.value != null
          ? (params.value >= 0 ? '#2ecc71' : '#e74c3c')
          : 'var(--text-secondary)',
      }),
    },
    {
      headerName: t('valuation.positions.prevValDate'),
      valueGetter: (params: any) => params.data?.previousValuation?.valuationDate,
      minWidth: 85,
      valueFormatter: (params: any) => formatDate(params.value || ''),
    },
    {
      headerName: t('valuation.positions.prevMtm'),
      valueGetter: (params: any) => params.data?.previousValuation?.mtmAmount ?? null,
      minWidth: 120,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        const currency = params.data?.previousValuation?.currency || params.data?.currency2 || '';
        return formatMtm(params.value, currency);
      },
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.mtmDelta'),
      valueGetter: (params: any) => {
        const current = params.data?.currentValuation?.mtmCcy2 ?? params.data?.currentValuation?.mtmAmount;
        const previous = params.data?.previousValuation?.mtmCcy2 ?? params.data?.previousValuation?.mtmAmount;
        if (current != null && previous != null) return current - previous;
        return null;
      },
      minWidth: 120,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        const currency = params.data?.currentValuation?.currency || params.data?.currency2 || '';
        const prefix = params.value >= 0 ? '+' : '';
        return currency ? `${currency} ${prefix}${formatNumber(params.value)}` : `${prefix}${formatNumber(params.value)}`;
      },
      cellStyle: (params: any) => ({
        fontFamily: 'monospace',
        color: params.value != null
          ? (params.value >= 0 ? '#2ecc71' : '#e74c3c')
          : 'var(--text-secondary)',
      }),
    },
    // --- Calculation-only columns (show — for manual) ---
    {
      headerName: t('valuation.positions.days'),
      valueGetter: (params: any) => params.data?.currentValuation?.daysToSettlement,
      minWidth: 45,
      type: 'rightAligned',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.spotRate'),
      valueGetter: (params: any) => params.data?.currentValuation?.spotRate,
      minWidth: 70,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatRate(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      colId: 'forwardRate',
      headerName: t('valuation.positions.forwardRate'),
      valueGetter: (params: any) => {
        const cv = params.data?.currentValuation;
        return cv?.blendedRate ?? cv?.impliedForwardRate;
      },
      minWidth: 70,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatRate(params.value),
      cellStyle: (params: any) => {
        const isAsiatico = params.data?.isAverageRateForward || params.data?.IsAverageRateForward;
        const hasBlended = params.data?.currentValuation?.blendedRate != null;
        return {
          fontFamily: 'monospace',
          ...(isAsiatico && hasBlended ? { cursor: 'context-menu', textDecoration: 'underline dotted', textUnderlineOffset: '3px' } : {}),
        };
      },
    },
    // --- CCY1 valuation columns ---
    {
      headerName: t('valuation.positions.rCcy1'),
      valueGetter: (params: any) => params.data?.currentValuation?.rCcy1,
      minWidth: 65,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        return `${(params.value * 100).toFixed(4)}%`;
      },
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.dfCcy1'),
      valueGetter: (params: any) => params.data?.currentValuation?.dfCcy1,
      minWidth: 80,
      type: 'rightAligned',
      valueFormatter: (params: any) => params.value != null ? params.value.toFixed(8) : '\u2014',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.dccCcy1'),
      valueGetter: (params: any) => params.data?.currentValuation?.dccCcy1,
      minWidth: 65,
    },
    {
      headerName: t('valuation.positions.mtmCcy1'),
      valueGetter: (params: any) => params.data?.currentValuation?.mtmCcy1,
      minWidth: 85,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    // --- CCY2 valuation columns ---
    {
      headerName: t('valuation.positions.rCcy2'),
      valueGetter: (params: any) => params.data?.currentValuation?.rCcy2,
      minWidth: 65,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        return `${(params.value * 100).toFixed(4)}%`;
      },
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.rCcy2Eff'),
      // Effective period rate = 1/DF - 1, derived from dfCcy2
      valueGetter: (params: any) => {
        const df = params.data?.currentValuation?.dfCcy2;
        if (df == null || df === 0) return null;
        return (1 / df) - 1;
      },
      minWidth: 80,
      type: 'rightAligned',
      valueFormatter: (params: any) => {
        if (params.value == null) return '\u2014';
        return `${(params.value * 100).toFixed(6)}%`;
      },
      cellStyle: { fontFamily: 'monospace', color: '#ff9800' },
    },
    {
      headerName: t('valuation.positions.dfCcy2'),
      valueGetter: (params: any) => params.data?.currentValuation?.dfCcy2,
      minWidth: 80,
      type: 'rightAligned',
      valueFormatter: (params: any) => params.value != null ? params.value.toFixed(8) : '\u2014',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.dccCcy2'),
      valueGetter: (params: any) => params.data?.currentValuation?.dccCcy2,
      minWidth: 65,
    },
    {
      headerName: t('valuation.positions.mtmCcy2'),
      valueGetter: (params: any) => params.data?.currentValuation?.mtmCcy2,
      minWidth: 100,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    // --- KAN-118: Methodology columns ---
    {
      headerName: t('valuation.positions.forwardRateSource'),
      valueGetter: (params: any) => params.data?.currentValuation?.forwardRateSource,
      minWidth: 80,
      cellRenderer: (params: any) => {
        if (!params.value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        const isFwdPoints = params.value === 'fwd_points';
        return (
          <span style={{
            padding: '0.1rem 0.35rem',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 600,
            background: isFwdPoints ? 'rgba(255,183,77,0.15)' : 'rgba(79,195,247,0.15)',
            color: isFwdPoints ? '#ffb74d' : '#4fc3f7',
          }}>
            {isFwdPoints ? t('valuation.positions.forwardRateSourceFwdPoints') : 'CIRP'}
          </span>
        );
      },
    },
    {
      headerName: t('valuation.positions.discountingMethod'),
      valueGetter: (params: any) => params.data?.currentValuation?.discountingMethod,
      minWidth: 80,
      cellRenderer: (params: any) => {
        if (!params.value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        const isNetPnl = params.value === 'net_pnl';
        return (
          <span style={{
            padding: '0.1rem 0.35rem',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 600,
            background: isNetPnl ? 'rgba(156,39,176,0.15)' : 'rgba(100,181,246,0.15)',
            color: isNetPnl ? '#ce93d8' : '#90caf9',
          }}>
            {isNetPnl ? t('valuation.positions.discountingMethodNetPnl') : t('valuation.positions.discountingMethodTwoLeg')}
          </span>
        );
      },
    },
    {
      headerName: t('valuation.positions.undiscountedPnl'),
      valueGetter: (params: any) => params.data?.currentValuation?.undiscountedPnl,
      minWidth: 110,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    // --- KAN-118: IFRS 9 columns ---
    {
      headerName: t('valuation.positions.spotAtTrade'),
      valueGetter: (params: any) => params.data?.currentValuation?.spotAtTrade,
      minWidth: 80,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatRate(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.forwardPointsAtTrade'),
      valueGetter: (params: any) => params.data?.currentValuation?.forwardPointsAtTrade,
      minWidth: 90,
      type: 'rightAligned',
      valueFormatter: (params: any) => params.value != null ? params.value.toFixed(4) : '—',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('valuation.positions.spotElement'),
      valueGetter: (params: any) => params.data?.currentValuation?.spotElement,
      minWidth: 110,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value),
      cellStyle: (params: any) => ({
        fontFamily: 'monospace',
        color: params.value != null
          ? (params.value >= 0 ? '#2ecc71' : '#e74c3c')
          : 'var(--text-secondary)',
      }),
    },
    {
      headerName: t('valuation.positions.forwardElement'),
      valueGetter: (params: any) => params.data?.currentValuation?.forwardElement,
      minWidth: 110,
      type: 'rightAligned',
      valueFormatter: (params: any) => formatNumber(params.value),
      cellStyle: { fontFamily: 'monospace' },
    },
  ], [t, groupingsConfig, translateEngineError]);

  // Previous Valuation grid: same columns but without Prev Val Date, Prev MtM, MtM Change
  const previousColumnDefs = useMemo<ColDef[]>(() => {
    const excludeHeaders = new Set([
      t('valuation.positions.prevValDate'),
      t('valuation.positions.prevMtm'),
      t('valuation.positions.mtmDelta'),
    ]);
    return columnDefs.filter((col) => !excludeHeaders.has(col.headerName as string));
  }, [columnDefs, t]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    filter: true,
  }), []);

  // ============================================
  // Render
  // ============================================

  return (
    <div className="accounting-tab-content" style={{ position: 'relative' }}>
      {/* Calculating overlay */}
      {(calculating || !!deletingUploadId || deletingAll) && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.45)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          borderRadius: '8px',
        }}>
          <div className="spinner" style={{ width: '36px', height: '36px', borderWidth: '3px' }} />
          <span style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 600 }}>
            {calculating
              ? t('valuation.positions.calculating', 'Calculating MtM...')
              : t('valuation.positions.deleting', 'Deleting...')}
          </span>
        </div>
      )}
      {/* Actions Section (collapsible) */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: executionExpanded ? '0.75rem' : 0 }}
          onClick={() => setExecutionExpanded(prev => !prev)}
        >
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', width: '12px', flexShrink: 0 }}>
            {executionExpanded ? '\u25BC' : '\u25B6'}
          </span>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
            {t('valuation.positions.executionSection')}
          </h3>
        </div>
        {executionExpanded && (<>
        {/* Date picker + Method selector row */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.positions.valuationDate')}
            </label>
            <input
              type="date"
              value={valuationDate}
              onChange={(e) => setValuationDate(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '0.4rem 0.6rem',
                color: 'var(--text-primary)',
                fontSize: '0.85rem',
              }}
            />
          </div>

          {/* Resolved as-of date display (KAN-223) */}
          {valuationDate && valuationDateMode !== 'system_date' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {t('valuation.positions.asOfDate', 'As-of Date')}
              </label>
              <div style={{
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '1.8rem',
              }}>
                {resolvingDate ? (
                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>...</span>
                ) : (
                  <>
                    <span style={{ fontWeight: 600 }}>{formatDate(resolvedAsOfDate)}</span>
                    {resolvedDateInfo?.isMonthEnd && (
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '3px',
                        background: 'rgba(40, 167, 69, 0.15)',
                        color: '#28a745',
                        fontWeight: 600,
                      }}>
                        {t('valuation.positions.monthEnd', 'Month-end')}
                      </span>
                    )}
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {valuationDateMode === 'previous_business_day'
                        ? t('admin.valuation.asOfDatePrevBD', 'Previous Business Day')
                        : valuationDateMode === 'end_of_previous_month'
                        ? t('admin.valuation.asOfDateEndOfPrevMonth', 'End of Previous Month')
                        : ''}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Method selector (segmented control) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.positions.methodLabel')}
            </label>
            <div style={{ display: 'flex' }}>
              <button
                onClick={() => {}}
                disabled
                style={{
                  padding: '0.4rem 1rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'default',
                  borderRadius: '6px 0 0 6px',
                  border: `1px solid ${method === 'calculate' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                  background: method === 'calculate' ? 'var(--accent-blue)' : 'transparent',
                  color: method === 'calculate' ? '#ffffff' : 'var(--text-secondary)',
                  opacity: 0.7,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.positions.methodCalculate')}
              </button>
              <button
                onClick={() => {}}
                disabled
                style={{
                  padding: '0.4rem 1rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'default',
                  borderRadius: '0 6px 6px 0',
                  border: `1px solid ${method === 'manual' ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                  borderLeft: 'none',
                  background: method === 'manual' ? 'var(--accent-blue)' : 'transparent',
                  color: method === 'manual' ? '#ffffff' : 'var(--text-secondary)',
                  opacity: 0.7,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.positions.methodManual')}
              </button>
            </div>
          </div>
        </div>

        {/* Method-specific controls */}
        {method === 'calculate' ? (
          <div>
            {/* Collapsible methodology panel */}
            <div style={{ marginBottom: '0.5rem' }}>
              <button
                onClick={() => setMethodologyExpanded(p => !p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  padding: '0.3rem 0.7rem',
                  color: 'var(--text-secondary)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: '0.65rem' }}>{methodologyExpanded ? '▲' : '▼'}</span>
                <span>{t('valuation.positions.methodologyLabel', 'Methodology')}</span>
                {!methodologyExpanded && (
                  <span style={{
                    marginLeft: '0.25rem',
                    padding: '0.1rem 0.5rem',
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    fontSize: '0.75rem',
                    color: 'var(--text-primary)',
                    fontFamily: 'monospace',
                  }}>
                    {forwardRateSource === 'cirp' ? 'CIRP' : 'Fwd Pts'}
                    {forwardRateSource === 'fwd_points' && <>{' · '}{{ market: 'Ask/Bid', mid: 'Mid', bid: 'Bid', ask: 'Ask' }[fwdPointsPrice]}</>}
                    {' · '}
                    {discountingMethod === 'two_leg' ? '2-Leg' : 'Net'}
                    {' · '}
                    {{ blended: 'Asiát: Blended', single_rate: 'Asiát: Single' }[asiaticoMethod]}
                    {forwardStartingEligibility === 'include_provisional' && <>{' · '}FwdStart: {{ single_rate: 'Single', blended: 'Blended' }[forwardStartingMethod]}</>}
                    {' · '}
                    {valuationCurrency}
                    {resolvedAsOfDate !== valuationDate && <>{' · '}<span style={{ color: 'var(--text-secondary)' }}>{t('valuation.positions.asOfDate', 'As-of Date')}: {formatDate(resolvedAsOfDate)}</span></>}
                    {resolvedMarketDataDate !== valuationDate && <>{' · '}<span style={{ color: 'var(--text-secondary)' }}>{t('valuation.positions.marketDataDateLabel', 'Mkt Data')}: {formatDate(resolvedMarketDataDate)}</span></>}
                    {resolvedSpotDate !== valuationDate && <>{' · '}<span style={{ color: 'var(--text-secondary)' }}>{t('admin.valuation.spotRate', 'Spot Price')}: {formatDate(resolvedSpotDate)}</span></>}
                    {resolvedUfDate !== valuationDate && <>{' · '}<span style={{ color: 'var(--text-secondary)' }}>{t('admin.valuation.ufRate', 'UF Value')}: {formatDate(resolvedUfDate)}</span></>}
                  </span>
                )}
              </button>

              {methodologyExpanded && (
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                }}>
                  {/* Forward Rate Source */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t('valuation.positions.forwardRateSource')}
                    </label>
                    <select
                      value={forwardRateSource}
                      onChange={(e) => setForwardRateSource(e.target.value as 'cirp' | 'fwd_points')}
                      disabled
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.4rem 0.6rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        cursor: 'default',
                        opacity: 0.7,
                      }}
                    >
                      <option value="cirp">{t('valuation.positions.forwardRateSourceCirp')}</option>
                      <option value="fwd_points">{t('valuation.positions.forwardRateSourceFwdPoints')}</option>
                    </select>
                  </div>

                  {/* Quote Side — only when fwd_points selected */}
                  {forwardRateSource === 'fwd_points' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {t('valuation.positions.fwdPointsPrice')}
                      </label>
                      <select
                        value={fwdPointsPrice}
                        onChange={(e) => setFwdPointsPrice(e.target.value as 'market' | 'mid' | 'bid' | 'ask')}
                        disabled
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          padding: '0.4rem 0.6rem',
                          color: 'var(--text-primary)',
                          fontSize: '0.85rem',
                          cursor: 'default',
                          opacity: 0.7,
                        }}
                      >
                        <option value="market">{t('valuation.positions.fwdPointsPriceMarket')}</option>
                        <option value="mid">{t('valuation.positions.fwdPointsPriceMid')}</option>
                        <option value="bid">{t('valuation.positions.fwdPointsPriceBid', 'Bid')}</option>
                        <option value="ask">{t('valuation.positions.fwdPointsPriceAsk', 'Ask')}</option>
                      </select>
                    </div>
                  )}

                  {/* Discounting Method */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t('valuation.positions.discountingMethod')}
                    </label>
                    <select
                      value={discountingMethod}
                      onChange={(e) => setDiscountingMethod(e.target.value as 'two_leg' | 'net_pnl')}
                      disabled
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.4rem 0.6rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        cursor: 'default',
                        opacity: 0.7,
                      }}
                    >
                      <option value="two_leg">{t('valuation.positions.discountingMethodTwoLeg')}</option>
                      <option value="net_pnl">{t('valuation.positions.discountingMethodNetPnl')}</option>
                    </select>
                  </div>

                  {/* Forward Asiático Method */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t('valuation.positions.asiaticoMethod', 'Fwd Asiático')}
                    </label>
                    <select
                      value={asiaticoMethod}
                      onChange={(e) => setAsiaticoMethod(e.target.value as 'blended' | 'single_rate')}
                      disabled
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.4rem 0.6rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        cursor: 'default',
                        opacity: 0.7,
                      }}
                    >
                      <option value="blended">{t('valuation.positions.asiaticoMethodBlended', 'Blended')}</option>
                      <option value="single_rate">{t('valuation.positions.asiaticoMethodSingleRate', 'Single Rate')}</option>
                    </select>
                  </div>

                  {/* Valuation Currency */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t('valuation.positions.valuationCurrency', 'Valuation Currency')}
                    </label>
                    <input
                      type="text"
                      value={valuationCurrency}
                      onChange={(e) => setValuationCurrency(e.target.value.toUpperCase().slice(0, 3))}
                      disabled
                      readOnly
                      maxLength={3}
                      placeholder="CLP"
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.4rem 0.6rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        width: '60px',
                        fontFamily: 'monospace',
                        opacity: 0.7,
                      }}
                    />
                  </div>

                  {/* Resolved dates (shown when any date differs from execution date) */}
                  {(resolvedAsOfDate !== valuationDate || resolvedMarketDataDate !== valuationDate || resolvedSpotDate !== valuationDate || resolvedUfDate !== valuationDate) && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.2rem',
                      borderLeft: '2px solid var(--border-color)',
                      paddingLeft: '0.75rem',
                      marginLeft: '0.25rem',
                    }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('valuation.positions.resolvedDates', 'Resolved Dates')}
                      </span>
                      {resolvedAsOfDate !== valuationDate && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {t('valuation.positions.asOfDate', 'As-of Date')}: {formatDate(resolvedAsOfDate)}
                        </span>
                      )}
                      {resolvedMarketDataDate !== valuationDate && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {t('valuation.positions.marketDataDateLabel', 'Mkt Data')}: {formatDate(resolvedMarketDataDate)}
                        </span>
                      )}
                      {resolvedSpotDate !== valuationDate && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {t('admin.valuation.spotRate', 'Spot Price')}: {formatDate(resolvedSpotDate)}
                        </span>
                      )}
                      {resolvedUfDate !== valuationDate && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {t('admin.valuation.ufRate', 'UF Value')}: {formatDate(resolvedUfDate)}
                        </span>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* Data requirements checklist + Calculate button */}
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>

              {/* Requirements list */}
              {(() => {
                const requirementsLoading = loadingForwardTrades || loadingCurves || loadingSpotRates;
                const MISSING_COLOR = '#a07840';
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: '180px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.15rem' }}>
                      {t('valuation.positions.requirementsTitle', 'Market Data Requirements')}
                      {resolvedMarketDataDate && (
                        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.3rem', fontFamily: 'monospace' }}>
                          {t('valuation.positions.requirementsForDate', 'for {{date}}', { date: formatDate(resolvedMarketDataDate) })}
                        </span>
                      )}
                    </span>
                    {requirementsLoading ? (
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', marginTop: '0.25rem' }} />
                    ) : valuationRequirements.length === 0 ? (
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>—</span>
                    ) : (
                      valuationRequirements.map(req => {
                        const navFn = req.category === 'spot' ? onNavigateToSpotRates
                          : req.category === 'fwd' ? onNavigateToFwdPoints
                          : onNavigateToIrCurves;
                        return (
                          <div key={req.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>•</span>
                            <span style={{ color: req.present ? 'var(--text-secondary)' : 'var(--text-primary)', flex: 1 }}>
                              {req.label}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: '0.75rem', color: req.present ? '#2ecc71' : MISSING_COLOR, minWidth: '1rem', textAlign: 'center' }}>
                              {req.present ? '✓' : '✗'}
                            </span>
                            {navFn && !req.present && (
                              <button
                                type="button"
                                onClick={navFn}
                                style={{
                                  fontSize: '0.72rem',
                                  padding: '0.1rem 0.5rem',
                                  border: `1px solid ${MISSING_COLOR}`,
                                  borderRadius: '4px',
                                  background: 'transparent',
                                  color: MISSING_COLOR,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('valuation.positions.updateButton', 'Actualizar')}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })()}

              {/* Force Recalculate + Calculate button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: 'auto' }}>
                {/* Force recalculate checkbox hidden — users should delete and recreate valuations instead
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="forceRecalculate"
                    checked={forceRecalculate}
                    onChange={(e) => setForceRecalculate(e.target.checked)}
                  />
                  <label htmlFor="forceRecalculate" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    {t('valuation.positions.forceRecalculate')}
                  </label>
                </div>
                */}

                {(() => {
                  const missingRequired = valuationRequirements.some(r => !r.present);
                  const isDisabled = calculating || missingRequired;
                  return (
                    <button
                      onClick={() => {
                        const asOf = resolvedAsOfDate || valuationDate;
                        const existsForDate = uploads.some(u => u.uploadDate === asOf);
                        if (existsForDate) {
                          setShowDuplicateWarning(true);
                        } else {
                          setShowCalculateModal(true);
                        }
                      }}
                      disabled={isDisabled}
                      style={{
                        background: calculating ? 'var(--bg-secondary)' : '#3b82f6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.75rem 1.5rem',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        minWidth: '80px',
                        opacity: missingRequired ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {calculating ? t('valuation.positions.calculating') : t('valuation.positions.calculateButton')}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              onClick={() => !uploadingFile && fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              style={{
                border: '2px dashed var(--border-color)',
                borderRadius: '6px',
                padding: '0.75rem 1.5rem',
                textAlign: 'center',
                cursor: uploadingFile ? 'wait' : 'pointer',
                transition: 'border-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              {uploadingFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: 18, height: 18,
                    border: '2px solid var(--border-color)',
                    borderTopColor: 'var(--accent-blue)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {t('valuation.positions.uploading')}
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>&#128196;</span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                    {t('valuation.positions.dragDropExcel')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    ({t('valuation.positions.acceptedFormatsExcel')})
                  </span>
                </div>
              )}
            </div>
            <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              {t('valuation.positions.overwriteNote')}
            </div>
          </>
        )}

        {/* Run result summary (after calculate) */}
        {lastRunResult && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: lastRunResult.errors.length > 0 ? 'rgba(255,152,0,0.1)' : 'rgba(76,175,80,0.1)',
            border: `1px solid ${lastRunResult.errors.length > 0 ? 'rgba(255,152,0,0.3)' : 'rgba(76,175,80,0.3)'}`,
            borderRadius: '4px',
            fontSize: '0.85rem',
          }}>
            <div>
              {t('valuation.positions.runSummary', {
                count: lastRunResult.tradeCount,
                time: lastRunResult.processingTimeSeconds,
              })}
              {lastRunResult.tradesSkipped > 0 && ` (${lastRunResult.tradesSkipped} ${t('valuation.positions.skipped')})`}
              {lastRunResult.tradesErrored > 0 && ` (${lastRunResult.tradesErrored} ${t('valuation.positions.errors')})`}
              {unvaluedTrades.length > 0 && ` (${unvaluedTrades.length} ${t('valuation.positions.ineligible')})`}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              {t('valuation.positions.curvesLabel')}: {(lastRunResult.curvesAvailable || lastRunResult.curvesUsed).join(', ') || t('valuation.positions.none')}
              {' | '}
              {t('valuation.positions.spotLabel')}: {lastRunResult.spotRates && Object.keys(lastRunResult.spotRates).length > 0
                ? Object.entries(lastRunResult.spotRates).map(([pair, rate]) => `${pair} ${formatRate(rate)}`).join(', ')
                : t('valuation.positions.none')}
            </div>
            {(() => {
              const displayErrors = lastRunResult.errors.filter(e => !e.error?.includes('Settles on valuation date'));
              return displayErrors.length > 0 ? (
                <div style={{ marginTop: '0.5rem', color: '#ff9800' }}>
                  {displayErrors.slice(0, 5).map((e, i) => (
                    <div key={i} style={{ fontSize: '0.8rem' }}>
                      {e.tradeNumber ? `${t('valuation.positions.tradeLabel')} ${e.tradeNumber}: ` : ''}{e.error}
                    </div>
                  ))}
                  {displayErrors.length > 5 && (
                    <div style={{ fontSize: '0.8rem' }}>
                      {t('valuation.positions.andMore', { count: displayErrors.length - 5 })}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Upload result summary (after manual upload) — consistent with calculate display */}
        {lastUploadResult && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: lastUploadResult.success ? 'rgba(76,175,80,0.1)' : 'rgba(231,76,60,0.1)',
            border: `1px solid ${lastUploadResult.success ? 'rgba(76,175,80,0.3)' : 'rgba(231,76,60,0.3)'}`,
            borderRadius: '4px',
            fontSize: '0.85rem',
          }}>
            <div>
              {t('valuation.positions.uploadResult', {
                valid: lastUploadResult.validRows,
                total: lastUploadResult.totalRows,
              })}
              {/* Only show non-workflow-state errors (parse errors, trade not found) */}
              {(() => {
                const realErrors = lastUploadResult.errors.filter(
                  (err: any) => !err.error?.includes('must be Confirmed')
                );
                if (realErrors.length === 0) return null;
                return (
                  <>
                    {' \u2014 '}
                    <button
                      onClick={() => setShowUploadErrors(!showUploadErrors)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--accent-blue)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        textDecoration: 'underline',
                      }}
                    >
                      {showUploadErrors ? t('valuation.positions.hideErrors') : t('valuation.positions.showErrors')}
                      {` (${realErrors.length})`}
                    </button>
                  </>
                );
              })()}
            </div>
            {showUploadErrors && (() => {
              const realErrors = lastUploadResult.errors.filter(
                (err: any) => !err.error?.includes('must be Confirmed')
              );
              if (realErrors.length === 0) return null;
              return (
                <div style={{
                  marginTop: '0.5rem',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  color: '#e74c3c',
                  fontSize: '0.8rem',
                }}>
                  {realErrors.map((err: any, i: number) => (
                    <div key={i} style={{ marginBottom: '0.15rem' }}>
                      {err.tradeNumber ? `[${err.tradeNumber}] ` : ''}{err.error}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
        </>)}
      </div>

      {/* ============================================ */}
      {/* Valuation Results Section                    */}
      {/* ============================================ */}

      <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem', color: 'var(--text-primary)' }}>
        {t('valuation.positions.valuationResults')}
      </h3>

      {/* Recent Batches table */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.positions.recentBatches')}
            </h3>
            <span style={{
              backgroundColor: 'var(--bg-tertiary)',
              padding: '0.1rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
            }}>
              {availableBatches.length}
            </span>
          </div>
        </div>

        {availableBatches.length > 0 ? (
          <div className="accounts-table-container">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>{t('valuation.positions.batchId')}</th>
                  <th>{t('valuation.positions.batchValDate')}</th>
                  <th>{t('valuation.positions.batchType')}</th>
                  <th>{t('valuation.positions.batchTrades')}</th>
                  <th>{t('valuation.positions.batchCurves')}</th>
                  <th>{t('valuation.positions.batchStatus')}</th>
                  <th style={{ width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {availableBatches.map(batch => (
                  <tr
                    key={batch.id}
                    className={selectedBatchId === batch.id ? 'batch-row-selected' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleBatchSelect(batch.id)}
                  >
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {batch.id ? batch.id.slice(0, 8) : '-'}
                    </td>
                    <td>{formatDate(batch.uploadDate)}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '3px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: batch.type === 'calculated' ? 'rgba(79,195,247,0.15)' : 'rgba(156,39,176,0.15)',
                        color: batch.type === 'calculated' ? '#4fc3f7' : '#ce93d8',
                      }}>
                        {batch.type === 'calculated'
                          ? t('valuation.positions.calculated')
                          : t('valuation.positions.manual')}
                      </span>
                    </td>
                    <td>{batch.tradeCount}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {batch.curvesUsed && batch.curvesUsed.length > 0
                        ? batch.curvesUsed.join(', ')
                        : '\u2014'}
                    </td>
                    <td>
                      <span
                        title={batch.status === 'partial' ? t('valuation.positions.statusPartialTooltip') : undefined}
                        style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: batch.status === 'partial' ? 'help' : undefined,
                          backgroundColor: batch.status === 'complete'
                            ? 'rgba(46, 204, 113, 0.2)'
                            : batch.status === 'partial'
                              ? 'rgba(255, 193, 7, 0.2)'
                              : batch.status === 'error'
                                ? 'rgba(231, 76, 60, 0.2)'
                                : 'rgba(255, 255, 255, 0.05)',
                          color: batch.status === 'complete'
                            ? '#2ecc71'
                            : batch.status === 'partial'
                              ? '#f39c12'
                              : batch.status === 'error'
                                ? '#e74c3c'
                                : 'var(--text-secondary)',
                        }}>
                        {batch.status === 'complete'
                          ? t('valuation.positions.statusComplete')
                          : batch.status === 'partial'
                            ? t('valuation.positions.statusPartial')
                            : batch.status === 'error'
                              ? t('valuation.positions.statusError')
                              : batch.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-icon btn-danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteUploadClick(batch); }}
                        disabled={deletingUploadId === batch.id}
                        title={t('common.delete')}
                        style={{ fontSize: '0.8rem' }}
                      >
                        {deletingUploadId === batch.id ? '...' : '\u2715'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            {t('valuation.positions.noValuations')}
          </div>
        )}
      </div>

      {/* Trade grid for selected batch */}
      {loadingTrades || loadingBatch ? (
        <div className="config-section" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: 24, height: 24, margin: '0 auto 0.75rem',
            border: '2px solid var(--border-color)',
            borderTopColor: 'var(--accent-blue)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            {loadingBatch ? t('valuation.positions.batchLoading') : `${t('common.loading')}...`}
          </span>
        </div>
      ) : selectedBatchTrades && selectedBatchTrades.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' }}>
          {/* Batch header with summary and export */}
          {(() => {
            const summary = getGroupSummary(selectedBatchTrades);
            const valDate = selectedBatchTrades[0]?.currentValuation?.valuationDate || '';
            const isCurrentBatch = selectedBatchId === currentUploadId;

            return (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: isCurrentBatch ? 'rgba(46,204,113,0.06)' : 'rgba(52,152,219,0.06)',
                  border: '1px solid #333',
                  borderLeft: `3px solid ${isCurrentBatch ? '#2ecc71' : '#3498db'}`,
                  borderRadius: '8px 8px 0 0',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}>
                  <span>{formatDate(valDate)}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                    ({selectedBatchTrades.length} {selectedBatchTrades.length === 1 ? 'trade' : 'trades'})
                  </span>
                  {/* Summary pushed right */}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem', fontSize: '0.8rem', fontWeight: 400, alignItems: 'center' }}>
                    <span>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('valuation.positions.totalMtm')}: </span>
                      <strong style={{
                        fontFamily: 'monospace',
                        color: summary.totalMtm >= 0 ? '#2ecc71' : '#e74c3c',
                      }}>
                        {summary.currency ? `${summary.currency} ` : ''}{formatNumber(summary.totalMtm)}
                      </strong>
                    </span>
                    {isCurrentBatch && summary.hasChange && (
                      <span>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('valuation.positions.totalChange')}: </span>
                        <strong style={{
                          fontFamily: 'monospace',
                          color: summary.totalChange >= 0 ? '#2ecc71' : '#e74c3c',
                        }}>
                          {summary.currency ? `${summary.currency} ` : ''}{summary.totalChange >= 0 ? '+' : ''}{formatNumber(summary.totalChange)}
                        </strong>
                      </span>
                    )}
                    <span
                      role="button"
                      title={t('valuation.positions.exportExcel')}
                      onClick={() => exportValuationToExcel(selectedBatchTrades, valDate)}
                      style={{
                        cursor: 'pointer',
                        padding: '0.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '0.5rem',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ color: '#28a745' }}>
                        <path d="M14 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM5.5 13H3V3h2.5v10zm4.5 0H6.5V3H10v10zm4 0h-3V3h3v10z"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <div style={{
                  background: '#1a1a1a',
                  border: '1px solid #333',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '0.25rem',
                }}>
                  <div ref={valuationGridContainerRef} className="ag-theme-alpine-dark" style={{
                    width: '100%',
                    height: `${Math.min(Math.max((selectedBatchTrades?.length || 0) * 42 + 56, 200), 600)}px`,
                  }}>
                    <AgGridReact
                      ref={valuationGridRef}
                      localeText={agGridLocale}
                      rowData={selectedBatchTrades}
                      columnDefs={isCurrentBatch ? columnDefs : previousColumnDefs}
                      defaultColDef={defaultColDef}
                      getRowId={(params) => `batch-${params.data.tradeId}`}
                      domLayout="normal"
                      animateRows={false}
                      onFirstDataRendered={onGridFirstDataRendered}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

        </div>
      ) : selectedBatchId ? null : (
        <div className="config-section" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
          {t('valuation.positions.noValuations')}
        </div>
      )}

      {/* AI Context Menu */}
      {showAiMenu && (
        <InlineMenu items={aiMenuItems} position={aiMenuPosition} onClose={closeAiMenu} />
      )}

      {/* Duplicate Valuation Warning Modal */}
      {showDuplicateWarning && (
        <div
          onClick={() => setShowDuplicateWarning(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: '#ff9800', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              &#9888; {t('valuation.positions.duplicateWarningTitle')}
            </h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.positions.duplicateWarningMessage', { date: formatDate(resolvedAsOfDate || valuationDate) })}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDuplicateWarning(false)}
                style={{
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('common.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Confirmation Modal */}
      {showCalculateModal && (
        <div
          onClick={() => setShowCalculateModal(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.positions.calculateConfirmTitle')}
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.positions.calculateConfirm', { date: formatDate(valuationDate) })}
            </p>
            {(resolvedAsOfDate !== valuationDate || resolvedMarketDataDate !== valuationDate || resolvedSpotDate !== valuationDate || resolvedUfDate !== valuationDate) && (
              <div style={{ margin: '0 0 0.75rem 0', padding: '0.5rem 0.75rem', background: 'rgba(59,130,246,0.08)', borderRadius: '4px', fontSize: '0.82rem', lineHeight: 1.6 }}>
                {resolvedAsOfDate !== valuationDate && (
                  <div><strong>{t('valuation.positions.asOfDate', 'As-of Date')}:</strong> {formatDate(resolvedAsOfDate)}
                    {valuationDateMode !== 'system_date' && (
                      <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                        ({valuationDateMode === 'previous_business_day'
                          ? t('admin.valuation.asOfDatePrevBD', 'Previous Business Day')
                          : t('admin.valuation.asOfDateEndOfPrevMonth', 'End of Previous Month')})
                      </span>
                    )}
                    {resolvedDateInfo?.isMonthEnd && (
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '3px',
                        background: 'rgba(40, 167, 69, 0.15)',
                        color: '#28a745',
                        fontWeight: 600,
                        marginLeft: '0.5rem',
                      }}>
                        {t('valuation.positions.monthEnd', 'Month-end')}
                      </span>
                    )}
                  </div>
                )}
                {resolvedMarketDataDate !== valuationDate && (
                  <div><strong>{t('valuation.positions.marketDataDateLabel', 'Mkt Data')}:</strong> {formatDate(resolvedMarketDataDate)}</div>
                )}
                {resolvedSpotDate !== valuationDate && (
                  <div><strong>{t('admin.valuation.spotRate', 'Spot Price')}:</strong> {formatDate(resolvedSpotDate)}</div>
                )}
                {resolvedUfDate !== valuationDate && (
                  <div><strong>{t('admin.valuation.ufRate', 'UF Value')}:</strong> {formatDate(resolvedUfDate)}</div>
                )}
              </div>
            )}
            {forceRecalculate ? (
              <>
                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#ff9800' }}>
                  {t('valuation.positions.calculateConfirmForce')}
                </p>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  {t('valuation.positions.overwriteNote')}
                </p>
              </>
            ) : (
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {t('valuation.positions.skipNote')}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                onClick={() => setShowCalculateModal(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
              <button
                onClick={executeCalculate}
                style={{
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.positions.calculateButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Upload Confirmation Modal */}
      {confirmDeleteUpload && (
        <div
          onClick={() => { setConfirmDeleteUpload(null); setDeleteCheckResult(null); }}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.positions.deleteUploadTitle')}
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.positions.deleteUploadMessage', { fileName: confirmDeleteUpload.fileName || `${confirmDeleteUpload.type === 'calculated' ? t('valuation.positions.calculated') : t('valuation.positions.manual')} — ${confirmDeleteUpload.uploadDate}` })}
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.positions.deleteUploadDetail')}
            </p>
            {checkingDelete && (
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                {t('valuation.positions.checkingAccountingDependencies')}
              </p>
            )}
            {deleteCheckResult?.hasAccountingEvents && (
              <div style={{
                margin: '0 0 1rem 0',
                padding: '0.75rem',
                background: 'rgba(255, 152, 0, 0.15)',
                border: '1px solid rgba(255, 152, 0, 0.4)',
                borderRadius: '6px',
              }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#ffb74d', fontWeight: 600 }}>
                  {t('valuation.positions.accountingWarningTitle')}
                </p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                  {t('valuation.positions.accountingWarningMessage', { count: deleteCheckResult.accountingEventCount })}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setConfirmDeleteUpload(null); setDeleteCheckResult(null); }}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
              <button
                onClick={executeDeleteUpload}
                disabled={!!deletingUploadId || checkingDelete}
                style={{
                  background: '#ef5350',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: (deletingUploadId || checkingDelete) ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {deletingUploadId ? '...' : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Valuations Confirmation Modal (demo mode) */}
      {confirmDeleteAll && (
        <div
          onClick={() => setConfirmDeleteAll(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: '#ef5350' }}>
              {t('valuation.positions.deleteAllTitle')}
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.positions.deleteAllMessage')}
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: '#ff9800', fontStyle: 'italic' }}>
              {t('valuation.positions.deleteAllWarning')}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteAll(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
              <button
                onClick={executeDeleteAll}
                style={{
                  background: '#ef5350',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.positions.deleteAllConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blended rate detail popover for Asiático trades */}
      {blendedRateDetail && blendedRateDetail.trade.currentValuation?.perDateRates && (
        <div
          ref={blendedRateDetailRef}
          style={{
            position: 'fixed',
            left: blendedRateDetail.position.x,
            top: blendedRateDetail.position.y,
            zIndex: 9999,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            padding: '12px 16px',
            fontFamily: "'Manrope', sans-serif",
            fontSize: '12px',
            color: '#e0e0e0',
            minWidth: '280px',
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#fff', fontSize: '13px' }}>
            {t('valuation.positions.blendedRateDetail.title')} — Trade {blendedRateDetail.trade.tradeNumber}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #444' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px 4px 0', fontWeight: 600, color: '#999' }}>
                  {t('valuation.positions.blendedRateDetail.date')}
                </th>
                <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: '#999' }}>
                  {t('valuation.positions.blendedRateDetail.rate')}
                </th>
                <th style={{ textAlign: 'right', padding: '4px 0 4px 8px', fontWeight: 600, color: '#999' }}>
                  {t('valuation.positions.blendedRateDetail.source')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(blendedRateDetail.trade.currentValuation.perDateRates as any[]).map((entry: any, i: number) => (
                <tr key={i}>
                  <td style={{ padding: '3px 8px 3px 0' }}>
                    {formatDate(entry.date || '')}
                  </td>
                  <td style={{ textAlign: 'right', padding: '3px 8px', fontFamily: 'monospace' }}>
                    {formatRate(entry.rate)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '3px 0 3px 8px' }}>
                    <span style={{
                      padding: '1px 6px',
                      borderRadius: '3px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: entry.source === 'realised' ? 'rgba(46,204,113,0.15)' : 'rgba(79,195,247,0.15)',
                      color: entry.source === 'realised' ? '#2ecc71' : '#4fc3f7',
                    }}>
                      {entry.source === 'realised'
                        ? t('valuation.positions.blendedRateDetail.realised')
                        : t('valuation.positions.blendedRateDetail.projected')
                      }
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid #444' }}>
                <td style={{ padding: '6px 8px 0 0', fontWeight: 'bold', color: '#fff' }}>
                  {t('valuation.positions.blendedRateDetail.average')}
                </td>
                <td style={{ textAlign: 'right', padding: '6px 8px 0', fontWeight: 'bold', color: '#fff', fontFamily: 'monospace' }}>
                  {formatRate(blendedRateDetail.trade.currentValuation?.blendedRate)}
                </td>
                <td style={{ padding: '6px 0 0 8px' }}>
                  <span style={{ fontSize: '0.65rem', color: '#888' }}>
                    ({blendedRateDetail.trade.currentValuation?.realisedCount || 0}R / {blendedRateDetail.trade.currentValuation?.projectedCount || 0}P)
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ValuationPositions;
